/**
 * Immunities, weaknesses and resistances applied to damage taken (#22; Foundry:
 * system/damage/iwr.ts applyIWR). Each damage instance is described by the roll options Foundry
 * gives it ("damage", its type, category, materials, persistent) plus the source's options, and
 * the maxima's IWR are tested against that description: an immunity removes the instance, the
 * highest applicable weakness adds its value, the highest applicable resistance takes its value
 * (doubled against its `doubleVs`), precision and splash shares have their own entries, and
 * weaknesses to things that do not normally deal damage apply once as untyped damage.
 *
 * Not carried: a weapon's ignored resistances and immunity or resistance redirects (a concussive
 * or vorpal weapon), which a Roll (#19) does not record yet; immunity to critical hits undoubles
 * half the instance, an approximation of Foundry's undoubled formula.
 */
import type { IWRView } from "../engine/engine";
import { resistanceValueAgainst, testIWR, type IWRData } from "../engine/iwr";
import { DAMAGE_TYPE_CATEGORIES } from "../engine/values";
import type { SessionMaxima } from "./maxima";

/** One instance of damage taken: its type and total, with the precision and splash shares and the materials it carries. */
export interface DamageInstanceInput {
  type: string;
  total: number;
  precision?: number;
  splash?: number;
  materials?: string[];
  persistent?: boolean;
}

export interface IWRApplication {
  category: "unaffected" | "immunity" | "weakness" | "resistance";
  /** The entry's label ("fire 5 (except cold iron)") or the type for unaffected damage. */
  label: string;
  adjustment: number;
}

export interface IWRResult {
  /** The damage after every application, never below 0. */
  total: number;
  applications: IWRApplication[];
}

const ORDER: Record<IWRApplication["category"], number> = { unaffected: 0, immunity: 1, weakness: 2, resistance: 3 };

function asData(view: IWRView): IWRData {
  return { ...view, customLabel: view.type === "custom" ? view.label : null };
}

/** Foundry's DamageInstance#formalDescription plus the source's roll options. */
export function describeInstance(instance: DamageInstanceInput, options: readonly string[] = []): Set<string> {
  const category = DAMAGE_TYPE_CATEGORIES[instance.type] ?? null;
  return new Set(["damage", `damage:type:${instance.type}`, ...(category ? [`damage:category:${category}`] : []), ...(instance.persistent ? ["damage:category:persistent"] : []), ...(instance.materials ?? []).map((m) => `damage:material:${m}`), ...options]);
}

/** Whether damage of a type affects the Character at all (Foundry's `isAffectedBy`): void healing swaps vitality and void. */
export function isAffectedBy(type: string, negativeHealing: boolean): boolean {
  if (type === "vitality") return negativeHealing;
  if (type === "void") return !negativeHealing;
  return true;
}

function highest<T extends { value: number | null }>(list: (T | null | undefined)[]): T | null {
  let best: T | null = null;
  for (const item of list) if (item && (!best || (item.value ?? 0) > (best.value ?? 0))) best = item;
  return best;
}

/** Apply the maxima's IWR to damage instances; `critical` marks a critical hit (immunity and resistance to critical hits). */
export function applyIWR(maxima: Pick<SessionMaxima, "iwr" | "negativeHealing">, instances: readonly DamageInstanceInput[], options: readonly string[] = [], critical = false): IWRResult {
  const { immunities, weaknesses, resistances } = maxima.iwr;
  const total = instances.reduce((sum, i) => sum + Math.max(0, i.total), 0);
  if (immunities.length === 0 && weaknesses.length === 0 && resistances.length === 0 && instances.every((i) => isAffectedBy(i.type, maxima.negativeHealing))) return { total, applications: [] };
  const test = (view: IWRView, description: Set<string> | string[]) => testIWR(asData(view), description);
  // A critical hit's roll options carry its outcome; immunity and resistance to critical hits test for it.
  const sourceOptions = critical ? [...options, "check:outcome:critical-success"] : [...options];
  const applyOnceWeaknesses = weaknesses.filter((w) => w.applyOnce && instances.some((i) => test(w, describeInstance(i, sourceOptions))));
  const damageWeaknesses = weaknesses.filter((w) => !applyOnceWeaknesses.includes(w));
  const applications: IWRApplication[] = instances.flatMap((instance): IWRApplication[] => {
    const description = describeInstance(instance, sourceOptions);
    const instanceTotal = Math.max(0, instance.total);
    if (!isAffectedBy(instance.type, maxima.negativeHealing)) return [{ category: "unaffected", label: instance.type, adjustment: -instanceTotal }];
    const applicableImmunities = immunities.filter((i) => i.type !== "critical-hits" && test(i, description));
    if (applicableImmunities.length > 0) return [{ category: "immunity", label: applicableImmunities[0]!.label, adjustment: -instanceTotal }];
    const out: IWRApplication[] = [];
    // Immunity to critical hits: the doubled half of the instance is undone (an approximation of Foundry's undoubled formula).
    const critImmunity = critical ? immunities.find((i) => i.type === "critical-hits" && test(i, description)) : undefined;
    const critImmuneTotal = critical ? Math.floor(instanceTotal / 2) : instanceTotal;
    const critImmunityApplies = !!critImmunity && critImmuneTotal < instanceTotal;
    if (critImmunityApplies) out.push({ category: "immunity", label: critImmunity!.label, adjustment: -(instanceTotal - critImmuneTotal) });
    const precisionImmunity = immunities.find((i) => i.type === "precision");
    const precisionDamage = critImmunityApplies ? Math.floor((instance.precision ?? 0) / 2) : instance.precision ?? 0;
    if (precisionDamage > 0 && precisionImmunity && test(precisionImmunity, [...description, "damage:component:precision"])) {
      const maxReducible = critImmunityApplies ? critImmuneTotal : instanceTotal;
      if (maxReducible > 0) out.push({ category: "immunity", label: precisionImmunity.label, adjustment: -Math.min(precisionDamage, maxReducible) });
    }
    const afterImmunities = Math.max(instanceTotal + out.reduce((sum, a) => sum + a.adjustment, 0), 0);
    if (afterImmunities === 0) return out;
    // Weaknesses: the highest of the main ones, the precision one and the splash one.
    const mainWeaknesses = damageWeaknesses.filter((w) => test(w, description));
    const splashWeakness = (instance.splash ?? 0) > 0 ? weaknesses.find((w) => w.type === "splash-damage") : null;
    const precisionWeakness = precisionDamage > 0 ? weaknesses.find((w) => w.type === "precision" && test(w, [...description, "damage:component:precision"])) : null;
    const weakness = highest([...mainWeaknesses, precisionWeakness, splashWeakness]);
    if (weakness) out.push({ category: "weakness", label: weakness.label, adjustment: weakness.value ?? 0 });
    const afterWeaknesses = afterImmunities + (weakness?.value ?? 0);
    // Resistances: the highest applicable, doubled where its doubleVs matches; critical hits and precision damage have their own, capped.
    const working: { label: string; value: number | null }[] = resistances.filter((r) => r.type !== "critical-hits" && r.type !== "precision" && test(r, description)).map((r) => ({ label: r.label, value: resistanceValueAgainst(asData(r), description) }));
    const criticalResistance = critical ? resistances.find((r) => r.type === "critical-hits") : undefined;
    if (criticalResistance && instanceTotal - critImmuneTotal > 0) working.push({ label: criticalResistance.label, value: Math.min(resistanceValueAgainst(asData(criticalResistance), description), instanceTotal - critImmuneTotal) });
    const precisionResistance = precisionDamage > 0 && !precisionImmunity ? resistances.find((r) => r.type === "precision" && test(r, [...description, "damage:component:precision"])) : undefined;
    if (precisionResistance) working.push({ label: precisionResistance.label, value: Math.min(resistanceValueAgainst(asData(precisionResistance), description), precisionDamage) });
    const resistance = highest(working);
    if (resistance && (resistance.value ?? 0) > 0) out.push({ category: "resistance", label: resistance.label, adjustment: -Math.min(afterWeaknesses, resistance.value ?? 0) });
    return out;
  }).concat(applyOnceWeaknesses.map((w): IWRApplication => ({ category: "weakness", label: w.label, adjustment: w.value ?? 0 })));
  applications.sort((a, b) => ORDER[a.category] - ORDER[b.category]);
  const adjustment = applications.reduce((sum, a) => sum + a.adjustment, 0);
  return { total: Math.max(total + adjustment, 0), applications };
}

/** "resistance fire 2: −2", "weakness cold iron 5: +5", "immunity fire: −8", "unaffected by vitality: −6". */
export function describeApplication(a: IWRApplication): string {
  const amount = a.adjustment > 0 ? `+${a.adjustment}` : a.adjustment < 0 ? `−${-a.adjustment}` : "0";
  return `${a.category === "unaffected" ? `unaffected by ${a.label}` : `${a.category} ${a.label}`}: ${amount}`;
}
